import React from "react";
import "./RelatedProducts.css";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../../Features/Cart/cartSlice";
import { Link, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import StoreData from "../../../Data/StoreData";
import { FiHeart } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaCartPlus } from "react-icons/fa";
import toast from "react-hot-toast";
import { addToWishList, removeFromWishList } from "../../../Features/Wishlist/wishListSlice";

const isPreciousMetal = (metalType) => {
  const preciousMetals = ['Gold', 'Rose Gold', 'Platinum', 'White Gold'];
  return preciousMetals.includes(metalType);
};

const RelatedProducts = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const { productId } = useParams(); // Get the current product ID from URL params

  // Convert string productId to number for comparison
  const currentProductId = parseInt(productId, 10);
  
  // Find the current product
  const currentProduct = StoreData.find(product => product.productID === currentProductId) || StoreData[0];
  
  // Function to count matching tags between products
  const getTagMatchCount = (product) => {
    if (!currentProduct.tags || !product.tags) return 0;
    
    return currentProduct.tags.filter(tag => 
      product.tags.includes(tag)
    ).length;
  };
  
  // Get related products by tag similarity
  const getRelatedProducts = () => {
    // Return empty array if no current product found
    if (!currentProduct) return [];
    
    // Get all other products (excluding current one)
    const otherProducts = StoreData.filter(p => p.productID !== currentProductId);
    
    // Sort products by number of matching tags (descending)
    const sortedByTags = [...otherProducts].sort((a, b) => {
      const aMatchCount = getTagMatchCount(a);
      const bMatchCount = getTagMatchCount(b);
      return bMatchCount - aMatchCount;
    });
    
    // If we have matching tags, return top 8 related products
    const relatedByTags = sortedByTags.filter(p => getTagMatchCount(p) > 0).slice(0, 8);
    
    // If we have less than 8 related products, add products from same category
    if (relatedByTags.length < 8) {
      const sameCategory = otherProducts
        .filter(p => p.category === currentProduct.category && !relatedByTags.includes(p))
        .slice(0, 8 - relatedByTags.length);
      
      return [...relatedByTags, ...sameCategory];
    }
    
    return relatedByTags;
  };
  
  // Get related products
  const relatedProducts = getRelatedProducts();
  
  // If we still don't have 8 products, get any products to fill
  const displayProducts = relatedProducts.length >= 8 
    ? relatedProducts 
    : [...relatedProducts, ...StoreData.filter(p => 
        p.productID !== currentProductId && 
        !relatedProducts.some(rp => rp.productID === p.productID)
      )].slice(0, 8);

  const handleWishlistClick = (product) => {
    if (isInWishlist(product.productID)) {
      dispatch(removeFromWishList(product));
      toast.success(`Removed from wishlist!`, {
        duration: 2000,
        style: {
          backgroundColor: "#07bc0c",
          color: "white",
        },
        iconTheme: {
          primary: "#fff",
          secondary: "#07bc0c",
        },
      });
    } else {
      dispatch(addToWishList(product));
      toast.success(`Added to wishlist!`, {
        duration: 2000,
        style: {
          backgroundColor: "#07bc0c",
          color: "white",
        },
        iconTheme: {
          primary: "#fff",
          secondary: "#07bc0c",
        },
      });
    }
  };

  const isInWishlist = (productID) => {
    return wishlistItems.some(item => item.productID === productID);
  };
  
  const handleAddToCart = (product) => {
    // Check if the product is made of precious metal
    if (isPreciousMetal(product.metalType)) {
      toast.error("Please contact us for current pricing on precious metal items", {
        duration: 3000,
        style: {
          backgroundColor: "#ff4b4b",
          color: "white",
        },
        iconTheme: {
          primary: "#fff",
          secondary: "#ff4b4b",
        },
      });
      return;
    }

    // Original cart logic for non-precious metals
    const productInCart = cartItems.find(
      (item) => item.productID === product.productID
    );

    if (productInCart && productInCart.quantity >= 20) {
      toast.error("Product limit reached", {
        duration: 2000,
        style: {
          backgroundColor: "#ff4b4b",
          color: "white",
        },
        iconTheme: {
          primary: "#fff",
          secondary: "#ff4b4b",
        },
      });
    } else {
      dispatch(addToCart(product));
      toast.success(`Added to cart!`, {
        duration: 2000,
        style: {
          backgroundColor: "#07bc0c",
          color: "white",
        },
        iconTheme: {
          primary: "#fff",
          secondary: "#07bc0c",
        },
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div className="relatedProductSection">
        <div className="relatedProducts">
          <h2>
            RELATED <span>PRODUCTS</span>
          </h2>
        </div>
        <div className="relatedProductSlider">
          <div className="swiper-button image-swiper-button-next">
            <IoIosArrowForward />
          </div>
          <div className="swiper-button image-swiper-button-prev">
            <IoIosArrowBack />
          </div>
          <Swiper
            slidesPerView={4}
            slidesPerGroup={4}
            spaceBetween={30}
            loop={true}
            navigation={{
              nextEl: ".image-swiper-button-next",
              prevEl: ".image-swiper-button-prev",
            }}
            modules={[Navigation]}
            breakpoints={{
              320: {
                slidesPerView: 2,
                slidesPerGroup: 2,
                spaceBetween: 14,
              },
              768: {
                slidesPerView: 3,
                slidesPerGroup: 3,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 4,
                slidesPerGroup: 4,
                spaceBetween: 30,
              },
            }}
          >
            {displayProducts.map((product) => {
              return (
                <SwiperSlide key={product.productID}>
                  <div className="rpContainer">
                    <div className="rpImages">
                      <Link to={`/product/${product.productID}`} onClick={scrollToTop}>
                        <img
                          src={product.frontImg}
                          alt={product.productName}
                          className="rpFrontImg"
                        />
                        <img
                          src={product.backImg}
                          className="rpBackImg"
                          alt={product.productName}
                        />
                      </Link>
                      {isPreciousMetal(product.metalType) ? (
                        <Link to="/contact">
                          <h4>Contact for Price</h4>
                        </Link>
                      ) : (
                        <h4 onClick={() => handleAddToCart(product)}>
                          Add to Cart
                        </h4>
                      )}
                    </div>
                    <div
                      className="rpImagesCart"
                      onClick={() => handleAddToCart(product)}
                    >
                      <FaCartPlus />
                    </div>

                    <div className="relatedProductInfo">
                      <div className="rpCategoryWishlist">
                        <p>{product.category}</p>
                        <FiHeart
                          onClick={() => handleWishlistClick(product)}
                          style={{
                            color: isInWishlist(product.productID) ? "red" : "#767676",
                            cursor: "pointer",
                          }}
                        />
                      </div>
                      <div className="productNameInfo">
                        <Link to={`/product/${product.productID}`} onClick={scrollToTop}>
                          <h5>{product.productName}</h5>
                        </Link>
                        <div className="flex items-center">
                          {isPreciousMetal(product.metalType) ? (
                            <span style={{ color: "#767676", fontWeight: "500" }}>
                              Price on inquiry
                            </span>
                          ) : (
                            <>
                              {product.isOnSale ? (
                                <>
                                  <span style={{ color: "#a52a2a", fontWeight: "500" }}>
                                    ${product.productPrice}
                                  </span>
                                  &nbsp;
                                  <span
                                    style={{
                                      color: "#767676",
                                      textDecoration: "line-through",
                                      textDecorationThickness: "1px"
                                    }}
                                  >
                                    ${product.originalPrice}
                                  </span>
                                </>
                              ) : (
                                <span style={{ color: "#767676" }}>
                                  ${product.productPrice}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="productRatingReviews">
                        <div className="productRatingStar">
                          {[...Array(5)].map((_, index) => (
                            <FaStar
                              key={index}
                              color={index < product.stars ? "#FEC78A" : "#C4C4C4"}
                              size={10}
                            />
                          ))}
                          <span>{product.productReviews}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </>
  );
};

export default RelatedProducts;